"use client";

import * as React from "react";
import { LoaderCircle } from "lucide-react";

import { cn } from "../../lib/utils";
import { refusalProps, withDisabledReason } from "./disabled-reason";

/* The Sirdab input. Composite: the wrapper owns the chrome (border, radius,
   focus), the <input> inside is bare, which is what lets a leading icon,
   the field, and a trailing button share one bordered box.

   Chrome: rest border alpha-200 → hover alpha-400 (every size) →
   focus-within ink border +
   0.5px ink ring (~1.5px of ink total, deliberately not the global 2px
   outline). Placeholder and leading icon are subtle; the icon lights to
   foreground with the field, so it focuses as a unit. Type is 16px on
   touch widths (no iOS zoom) and 14px from sm up, regular weight.

   Disabled fields DIM (opacity), unlike buttons' solid pair: two
   deliberate patterns, one per family; do not unify them. Pointer events stay on so
   the not-allowed cursor reads and a trailing button can stay live.

   The loading spinner is a TRAILING SLOT MEMBER (a 32px centered box, like
   an icon button), not an overlay, so spinner and trailing icons align in
   one column across stacked inputs.

   TWO leading slots, because they are two different things. `leading` is
   the ICON gutter: quieted at rest, lighting with focus, dimmed when the
   field is. `leadingControl` is a live CONTROL sharing the box (the phone
   field's country select) and must never be quieted: it owns its own
   hover and press ladder, and dimming it would say it is not pressable.
   It mirrors the trailing slot's snug padding (ps-1 against pe-1) so a
   32px control lands 4+32+4 inside the 40px box.

   readOnly is a DECISION, not disabled: the field stays focusable and
   copyable (focus ring intact) but loses the hover affordance. Hover
   promises editability. Pair it with a trailing copy button.

   Disabled dims the input and leading icon only: the trailing slot stays
   at full opacity because it stays LIVE (a copy button next to a disabled
   field still works); dimming it would say otherwise. The hairline border
   stays undimmed too.

   The inner field is data-slot="input-field", NOT "input": shadcn's slot
   name would put the bridge's standalone-input padding on our bare field
   (it double-padded to 23px once; 12px is the spec).

   INVALID NEVER COLOURS THE BORDER. Errors are destructive TEXT under the
   field; the chrome stays neutral.
   Error text is text-caption + text-destructive, and KEEPS the medium
   weight [decision]: with neutral chrome the text carries the entire
   error signal, so it stays emphatic while other captions dropped to the
   400 quiet role.

   Sizes: sm 32 dense rows · md 36 compact forms · default 40 forms
   (rounded-xl: the 40px rung is the house default [decision]) ·
   search 40 · touch 44 auth/mobile. Every size hovers alpha-400. */

/* Every size hovers to alpha-400: one input, one hover, standardized on
   the deeper rung. Checkbox/radio keep the gentler -form rung: a 16px box
   at 400 shouts. */
const SIZE_CLASSES = {
  sm: "h-control rounded-lg px-2.5 hover:not-focus-within:not-has-[:is(input,textarea,select):read-only]:border-input-border-hover",
  md: "h-control-lg rounded-lg px-3 hover:not-focus-within:not-has-[:is(input,textarea,select):read-only]:border-input-border-hover",
  default:
    "h-control-xl rounded-xl px-3 hover:not-focus-within:not-has-[:is(input,textarea,select):read-only]:border-input-border-hover",
  search:
    "h-control-xl rounded-xl px-3 hover:not-focus-within:not-has-[:is(input,textarea,select):read-only]:border-input-border-hover",
  touch:
    "h-control-touch rounded-xl px-3 hover:not-focus-within:not-has-[:is(input,textarea,select):read-only]:border-input-border-hover",
} as const;

/* Leading-icon scale (the 40px search size renders a 20px icon) and the
   trailing-button pairing that keeps the slot snug:
   sm/md → icon 16px, trailing size="icon-sm" (28px)
   default/search/touch → icon 20px, trailing size="icon" (32px) */
const LEADING_ICON = {
  sm: "[&_svg]:size-4",
  md: "[&_svg]:size-4",
  default: "[&_svg]:size-5",
  search: "[&_svg]:size-5",
  touch: "[&_svg]:size-5",
} as const;

/**
 * Usage:
 *
 *     <Input placeholder="اسم المنتج" />
 *     <Input size="search" leading={<Icon icon={Search} />} placeholder="Search…" />
 *     <Input size="search" leading={<Icon icon={Search} />} loading />
 *     <Input size="search" trailing={<Button variant="ghost" size="icon" …/>} />
 *     <Input leadingControl={<CountrySelect …/>} inputMode="tel" />
 *
 * Trailing pairing: sm/md wrappers take size="icon-sm" (28px),
 * default/search/touch take size="icon" (32px: 4+32+4 fills the 40px
 * box exactly). The slot stays a plain node on
 * purpose: cloning to auto-size would guess wrong for non-Button content.
 *
 * A Button BESIDE the input (same row, outside the box) is size="xl",
 * 40px matching the default; see the sizing rule in button.tsx.
 *
 * className is for LAYOUT only: width, margins, grid placement; size and
 * chrome come from the props. `inputClassName` reaches the bare inner
 * element (e.g. "latin tabular" for identifier fields). Leading icons
 * light up with focus automatically. Invalid fields keep neutral chrome.
 * Render destructive caption text below instead. Numeric fields should
 * normalize digits: onChange={(e) => setValue(toWesternDigits(e.target.value))}.
 */
function Input({
  className,
  inputClassName,
  type,
  bare = false,
  size = "default",
  leading,
  leadingControl,
  trailing,
  loading = false,
  disabledReason,
  ...props
}: Omit<React.ComponentProps<"input">, "size" | "children"> & {
  /** WHY this field is unavailable, on a tooltip. Implies disabled.
        A field refuses with readOnly rather than the disabled
        attribute, so it stays focusable and the reason stays readable
        (see disabled-reason.tsx). */
  disabledReason?: React.ReactNode;
  inputClassName?: string;
  /** The inline-edit mode, mirroring Textarea's: no border and no box of
        its own, so an always-editable title reads as text and the caller
        owns the box. Focus is the global ink outline rather than the
        bordered field's border+ring, so clicking or typing is unmistakable
        without chrome at rest. Without this mode, swapping text for a field
        on click changes height, type and width at once: three layout
        shifts in one tap. */
  bare?: boolean;
  size?: keyof typeof SIZE_CLASSES;
  leading?: React.ReactNode;
  /** A live control inside the box on the leading side, unquieted.
        `leading` is for icons; this is for something the user presses. */
  leadingControl?: React.ReactNode;
  trailing?: React.ReactNode;
  loading?: boolean;
}) {
  const field = (
    <div
      data-slot="input-wrapper"
      data-size={size}
      className={cn(
        "group flex w-full min-w-0 cursor-text items-center gap-2 bg-transparent transition-colors duration-75",
        bare
          ? ""
          : cn(
              "border-input border",
              SIZE_CLASSES[size],
              "focus-within:border-ring focus-within:ring-input-focus",
            ),
        (trailing != null || loading) && "pe-1",
        leadingControl != null && "ps-1",
        "has-disabled:cursor-not-allowed",
        className,
      )}
      /* The whole bordered box is the field: clicks on the padding and
               icon gutter forward focus to the input (only the trailing
               slot's own controls keep their clicks). preventDefault stops
               the mousedown from blurring the field it just focused. */
      onPointerDown={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest("input, button, a, [role=button]")) return;
        const field = event.currentTarget.querySelector<HTMLInputElement>(
          'input[data-slot="input-field"]',
        );
        if (!field || field.disabled) return;
        event.preventDefault();
        field.focus();
      }}
    >
      {leadingControl != null ? (
        <span
          data-slot="input-leading-control"
          className="flex shrink-0 items-center"
        >
          {leadingControl}
        </span>
      ) : null}
      {leading ? (
        <span
          className={cn(
            // icon-quiet (solid ink + opacity), not text-subtle: alpha ink
            // double-paints where icon strokes cross.
            "icon-quiet group-has-disabled:icon-quiet-disabled flex shrink-0 items-center transition-opacity duration-75 group-focus-within:opacity-100",
            LEADING_ICON[size],
          )}
        >
          {leading}
        </span>
      ) : null}
      <span className="relative min-w-0 grow">
        <input
          type={type}
          data-slot="input-field"
          className={cn(
            // No file:* styling on purpose: uploads are a ghost
            // button + hidden <input type="file">, never a styled
            // file control (the house pattern).
            "text-body-lg selection:bg-primary selection:text-primary-foreground placeholder:text-subtle sm:text-body-quiet size-full min-w-0 bg-transparent disabled:cursor-not-allowed disabled:opacity-50",
            /* A bordered field suppresses the global focus
                           outline because its own ink border + 0.5px ring
                           already paint ~1.5px of ink, and both together
                           read as a doubled edge. A BARE field has no
                           border to pair with, so it keeps the global
                           outline: same ink weight, and the only cue that
                           the caret is live. */
            bare
              ? // The radius is invisible at rest (nothing is
                // painted) and only shapes the focus outline,
                // so it follows our scale instead of boxing
                // the text in hard corners.
                "rounded-sm"
              : "outline-none focus-visible:outline-none",
            inputClassName,
          )}
          {...props}
          {...refusalProps(disabledReason, "field")}
        />
      </span>
      {/* In-flow trailing member, sized like an icon button's slot so
                spinner and trailing icons share one vertical column (an
                overlay inside the field would need a halo plus reserved
                text padding, the pattern this replaces; in-flow needs
                neither). Renders BEFORE trailing content: spinner, then
                button. */}
      {loading ? (
        // Ink, not the quiet ladder: a spinner is ACTIVITY, and it
        // must read over whatever the field shows. [decision]
        // aria-hidden: decorative motion; loading state is the
        // caller's to announce (aria-busy on the form region).
        <span
          aria-hidden
          className="flex size-8 shrink-0 items-center justify-center text-foreground"
        >
          <LoaderCircle className="size-4 animate-spin" />
        </span>
      ) : null}
      {trailing}
    </div>
  );

  return withDisabledReason(field, disabledReason, "block w-full");
}

export { Input };
